import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { claimPrompt, releasePrompt, getActivePrompt } from '@/pwa/promptCoordinator';
import { useMyWhatsApp, WHATSAPP_PHONE_ANCHOR } from '@/components/comm/WhatsAppAlertsSection';

const TOAST_ID = 'whatsapp-phone-invite';
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;
const key = (userId: string) => `whatsapp-phone-invite-snoozed:${userId}`;
const EXCLUDED = [
  '/login', '/escolher-ambiente', '/esqueci-senha', '/redefinir-senha', '/cadastro-corretor',
  '/corretor', '/portal-corretor', '/super-admin', '/politica-de-privacidade', '/diagnostico', '/inicio/perfil',
];
const isExcluded = (path: string) => path === '/' || EXCLUDED.some((p) => path === p || path.startsWith(p + '/'));

const otherPromptVisible = () =>
  (getActivePrompt() !== null && getActivePrompt() !== 'whatsapp-phone') ||
  Array.from(document.querySelectorAll('button')).some((b) =>
    /^(Atualizar agora|Atualizando\.\.\.)$/.test((b.textContent || '').trim()));

/** Convite discreto para quem participa do WhatsApp do sistema e ainda não tem telefone válido. */
export const WhatsAppPhoneInvite: React.FC = () => {
  const { user } = useSupabaseAuth();
  const userId = user?.id;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const excluded = isExcluded(pathname);
  const { data } = useMyWhatsApp(!!userId && !excluded);
  const shownRef = useRef(false);
  const needsPhone = !!data?.eligible && !data.whatsapp_phone && data.whatsapp_status !== 'opted_out';

  useEffect(() => {
    if (excluded) { toast.dismiss(TOAST_ID); releasePrompt('whatsapp-phone'); }
  }, [excluded]);

  useEffect(() => {
    if (shownRef.current || !userId || excluded || !needsPhone) return;
    try {
      const last = Number(localStorage.getItem(key(userId)) || 0);
      if (last && Date.now() - last < SNOOZE_MS) return;
    } catch { /* ignore */ }

    let timer: number;
    const tryShow = (attempt: number) => {
      if (attempt > 30) return;
      if (otherPromptVisible() || !claimPrompt('whatsapp-phone')) {
        timer = window.setTimeout(() => tryShow(attempt + 1), 5000);
        return;
      }
      shownRef.current = true;
      show(userId);
    };
    timer = window.setTimeout(() => tryShow(0), 6000);
    return () => window.clearTimeout(timer);
  }, [userId, excluded, needsPhone]);

  const show = (uid: string) => {
    const close = () => { toast.dismiss(TOAST_ID); releasePrompt('whatsapp-phone'); };
    const later = () => {
      try { localStorage.setItem(key(uid), String(Date.now())); } catch { /* ignore */ }
      close();
    };
    const go = () => {
      close();
      navigate('/inicio/perfil');
      const focus = (n: number) => window.setTimeout(() => {
        const el = document.getElementById(WHATSAPP_PHONE_ANCHOR);
        if (!el) { if (n < 20) focus(n + 1); return; }
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        (document.getElementById('whatsapp-phone-input') as HTMLInputElement | null)?.focus({ preventScroll: true });
      }, 300);
      focus(0);
    };

    toast.custom(() => (
      <div className="w-[min(360px,calc(100vw-2rem))] rounded-lg border border-border bg-background p-4 text-foreground shadow-lg">
        <div className="flex gap-3">
          <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground">Cadastre seu WhatsApp para receber avisos importantes do sistema</p>
            <div className="mt-3 flex items-center gap-2">
              <Button size="sm" className="h-9 flex-[1.4] font-semibold" onClick={go}>Cadastrar WhatsApp</Button>
              <Button size="sm" variant="ghost" className="h-9 flex-1 bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={later}>
                Agora não
              </Button>
            </div>
          </div>
        </div>
      </div>
    ), { id: TOAST_ID, duration: Infinity, position: 'bottom-center', style: { marginBottom: 'env(safe-area-inset-bottom)' }, unstyled: true, className: '!bg-transparent !border-0 !shadow-none !p-0' });
  };

  return null;
};

export default WhatsAppPhoneInvite;
